import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import {
  Shield,
  Download,
  Filter,
  Users,
  Check,
  Clock,
  AlertTriangle,
  XCircle,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { recruitingAPI } from "../services/recruitingApi";
import SearchInput from "../components/SearchInput";
import "./GdprReport.css";

interface GdprReportData {
  summary: {
    total_candidates: number;
    consent_none: number;
    consent_pending: number;
    consent_given: number;
    consent_withdrawn: number;
    consent_deleted: number;
    grandfathered_count: number;
  };
  candidates: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    gdpr_consent_status: string;
    gdpr_grandfathered: boolean;
    consent_given_at?: string;
    consent_withdrawn_at?: string;
    created_at: string;
  }[];
}

interface GdprReportResponse {
  success: boolean;
  data: GdprReportData;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  none: <XCircle size={16} />,
  pending: <Clock size={16} />,
  given: <Check size={16} />,
  withdrawn: <AlertTriangle size={16} />,
  deleted: <Trash2 size={16} />,
};

const STATUS_LABELS: Record<string, string> = {
  none: "No Consent",
  pending: "Pending",
  given: "Given",
  withdrawn: "Withdrawn",
  deleted: "Deleted",
};

const STATUS_COLORS: Record<string, string> = {
  none: "status-none",
  pending: "status-pending",
  given: "status-given",
  withdrawn: "status-withdrawn",
  deleted: "status-deleted",
};

const GdprReport: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Fetch GDPR report data
  const {
    data: reportData,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<GdprReportResponse>(
    ["gdpr-report", statusFilter],
    () =>
      recruitingAPI.getGdprReport({
        status: statusFilter !== "all" ? statusFilter : undefined,
      }) as Promise<GdprReportResponse>,
    {
      staleTime: 60000, // 1 minute
    },
  );

  const handleExportCSV = () => {
    if (!reportData?.data?.candidates) return;

    const headers = [
      "ID",
      "First Name",
      "Last Name",
      "Email",
      "GDPR Status",
      "Grandfathered",
      "Consent Given At",
      "Consent Withdrawn At",
      "Created At",
    ];
    const rows = reportData.data.candidates.map((c) => [
      c.id,
      c.first_name,
      c.last_name,
      c.email,
      STATUS_LABELS[c.gdpr_consent_status] || c.gdpr_consent_status,
      c.gdpr_grandfathered ? "Yes" : "No",
      c.consent_given_at || "",
      c.consent_withdrawn_at || "",
      c.created_at,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `gdpr-report-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const summary = reportData?.data?.summary;
  const candidates = reportData?.data?.candidates || [];

  // Filter candidates by search term
  const filteredCandidates = candidates.filter((c) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      c.first_name.toLowerCase().includes(term) ||
      c.last_name.toLowerCase().includes(term) ||
      c.email?.toLowerCase().includes(term)
    );
  });

  if (isLoading) {
    return (
      <div className="gdpr-report">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t("gdprReport.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gdpr-report">
      {/* Header */}
      <div className="report-header">
        <div className="header-info">
          <h1>
            <Shield size={28} /> {t("gdprReport.title")}
          </h1>
          <p>{t("gdprReport.subtitle")}</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw size={16} className={isRefetching ? "spinning" : ""} />
            {isRefetching
              ? t("gdprReport.actions.refreshing")
              : t("gdprReport.actions.refresh")}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleExportCSV}
            disabled={!candidates.length}
          >
            <Download size={16} /> {t("gdprReport.actions.exportCsv")}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="summary-cards">
          <div className="summary-card total">
            <div className="card-icon">
              <Users size={24} />
            </div>
            <div className="card-content">
              <span className="card-value">{summary.total_candidates}</span>
              <span className="card-label">
                {t("gdprReport.summary.totalCandidates")}
              </span>
            </div>
          </div>

          <div className="summary-card given">
            <div className="card-icon">
              <Check size={24} />
            </div>
            <div className="card-content">
              <span className="card-value">{summary.consent_given}</span>
              <span className="card-label">
                {t("gdprReport.summary.consentGiven")}
              </span>
            </div>
          </div>

          <div className="summary-card pending">
            <div className="card-icon">
              <Clock size={24} />
            </div>
            <div className="card-content">
              <span className="card-value">{summary.consent_pending}</span>
              <span className="card-label">
                {t("gdprReport.summary.pending")}
              </span>
            </div>
          </div>

          <div className="summary-card none">
            <div className="card-icon">
              <XCircle size={24} />
            </div>
            <div className="card-content">
              <span className="card-value">{summary.consent_none}</span>
              <span className="card-label">
                {t("gdprReport.summary.noConsent")}
              </span>
            </div>
          </div>

          <div className="summary-card withdrawn">
            <div className="card-icon">
              <AlertTriangle size={24} />
            </div>
            <div className="card-content">
              <span className="card-value">{summary.consent_withdrawn}</span>
              <span className="card-label">
                {t("gdprReport.summary.withdrawn")}
              </span>
            </div>
          </div>

          <div className="summary-card deleted">
            <div className="card-icon">
              <Trash2 size={24} />
            </div>
            <div className="card-content">
              <span className="card-value">{summary.consent_deleted}</span>
              <span className="card-label">
                {t("gdprReport.summary.deleted")}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Grandfathered Info */}
      {summary && summary.grandfathered_count > 0 && (
        <div className="grandfathered-info">
          <Shield size={16} />
          <span>
            <strong>{summary.grandfathered_count}</strong>{" "}
            {t("gdprReport.grandfathered")}
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="report-filters">
        <div className="filter-group">
          <label htmlFor="status-filter">
            <Filter size={16} /> {t("gdprReport.filters.byStatus")}:
          </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t("gdprReport.filters.allStatuses")}</option>
            <option value="none">{t("gdprReport.filters.noConsent")}</option>
            <option value="pending">{t("gdprReport.filters.pending")}</option>
            <option value="given">{t("gdprReport.filters.given")}</option>
            <option value="withdrawn">
              {t("gdprReport.filters.withdrawn")}
            </option>
            <option value="deleted">{t("gdprReport.filters.deleted")}</option>
          </select>
        </div>

        <div className="filter-group search">
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t("gdprReport.filters.searchPlaceholder")}
            ariaLabel={t("gdprReport.filters.searchPlaceholder")}
            showClearButton
          />
        </div>
      </div>

      {/* Candidates Table */}
      <div className="candidates-table-container">
        <table className="candidates-table">
          <thead>
            <tr>
              <th>{t("gdprReport.table.candidate")}</th>
              <th>{t("gdprReport.table.email")}</th>
              <th>{t("gdprReport.table.gdprStatus")}</th>
              <th>{t("gdprReport.table.consentDate")}</th>
              <th>{t("gdprReport.table.created")}</th>
              <th>{t("gdprReport.table.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredCandidates.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-row">
                  {searchTerm
                    ? t("gdprReport.noResults.withSearch")
                    : t("gdprReport.noResults.empty")}
                </td>
              </tr>
            ) : (
              filteredCandidates.map((candidate) => (
                <tr key={candidate.id}>
                  <td>
                    <div className="candidate-name">
                      {candidate.first_name} {candidate.last_name}
                      {candidate.gdpr_grandfathered && (
                        <span
                          className="grandfathered-badge"
                          title={t("gdprReport.grandfatheredBadge")}
                        >
                          <Shield size={12} />
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{candidate.email || "N/A"}</td>
                  <td>
                    <span
                      className={`status-badge ${STATUS_COLORS[candidate.gdpr_consent_status] || ""}`}
                    >
                      {STATUS_ICONS[candidate.gdpr_consent_status]}
                      {STATUS_LABELS[candidate.gdpr_consent_status] ||
                        candidate.gdpr_consent_status}
                    </span>
                  </td>
                  <td>
                    {candidate.consent_given_at
                      ? new Date(
                          candidate.consent_given_at,
                        ).toLocaleDateString()
                      : candidate.consent_withdrawn_at
                        ? new Date(
                            candidate.consent_withdrawn_at,
                          ).toLocaleDateString()
                        : "-"}
                  </td>
                  <td>{new Date(candidate.created_at).toLocaleDateString()}</td>
                  <td>
                    <a
                      href={`/candidates/${candidate.id}?tab=gdpr`}
                      className="view-link"
                    >
                      {t("gdprReport.actions.viewGdpr")}
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Results Count */}
      <div className="results-count">
        {t("gdprReport.resultsCount", {
          showing: filteredCandidates.length,
          total: candidates.length,
        })}
      </div>
    </div>
  );
};

export default GdprReport;
